import { useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

import {
  AppointmentInfoTypeIcon,
  PractitionerInfoTypeIcon,
} from "@assets/icons";
import { RepetitionIcon } from "@assets/icons/RepetitionIcon";
import {
  CardItemStyled,
  CardTitle,
  KMdText,
  KMdTextSecondary65,
  KTag,
} from "@components";
import { KContext } from "@context";
import { useCustomNavigate } from "@hooks";
import {
  Color,
  ECardType,
  ICardAppointmentRes,
  IItemCardProps,
  IParams,
} from "@types";
import {
  formatAppointmentDateStr,
  getFullDateStr,
  isFuture,
  isPast
} from "@utils/index";
import { Badge, Flex } from "antd";
import { CardItemContainer } from "..";

interface IAppointmentItem extends IItemCardProps<ICardAppointmentRes> {
  isPastItem?: boolean;
  isShowTimeRepetition?: boolean;
}

export const AppointmentItem = ({
  card,
  showIcon,
  showMetadata,
  isDetail,
  showBorder,
  isPastItem = false,
  isShowTimeRepetition = false,
}: IAppointmentItem) => {
  const { navigate } = useCustomNavigate();
  const { cardId } = useParams<IParams>();
  const { setCardTypeSelected, navigateDetailCard } = useContext(KContext);
  const {
    _id,
    title,
    practitionerName,
    startTime,
    upcomingAppointmentTime,
    repetition,
  } = card;

  const nextSeries = useMemo(() => {
    if (!repetition?.isSetRepetition) return null;

    const futureDates = [startTime, ...repetition?.sequenceDates].filter(
      isFuture
    );
    const sortedDate = futureDates.sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    return formatAppointmentDateStr(sortedDate[1]);
  }, [repetition, startTime]);

  const repetitionCount = useMemo(() => {
    if (!repetition?.sequenceDates.length) return null;

    const allDates = [startTime, ...repetition?.sequenceDates];

    const pastTimeCount = allDates.filter(isPast).length;

    if (pastTimeCount === allDates.length) {
      const count = allDates.length;
      if (!count) return null;
      return count < 10 ? `0${count}` : `${count}`;
    }
    
    // display sequences of upcoming occurrence/total of series(original+repetitions)
    return `${pastTimeCount + 1} / ${allDates.length}`;
  }, [repetition?.sequenceDates, startTime]);

  const timeRangeSeries = useMemo(() => {
    if (!isPastItem) return null;

    if (
      repetition?.sequenceDates.length &&
      repetition?.sequenceDates.length < 2
    ) {
      return formatAppointmentDateStr(repetition?.sequenceDates[0]);
    }

    return getFullDateStr(
      repetition?.sequenceDates[0],
      repetition?.sequenceDates[repetition?.sequenceDates.length - 1]
    );
  }, [repetition, isPastItem]);

  const onClickItem = () => {
    if (cardId === _id) return;
    if (!isDetail) {
      return navigate(_id, undefined, () => {
        setCardTypeSelected(ECardType.Appointments);
      });
    }
    navigateDetailCard(ECardType.Appointments, _id);
  };

  return (
    <CardItemStyled
      $isActive={cardId === _id}
      onClick={onClickItem}
      $showBorder={showBorder}
      style={{ ...(isPastItem && { backgroundColor: Color.Secondary10 }) }}
    >
      <CardItemContainer showMetadata={showMetadata} card={card} gap={8}>
        <Flex vertical gap={4}>
          <CardTitle
            icon={showIcon && <AppointmentInfoTypeIcon />}
            suffixIcon={
              !!repetition?.sequenceDates.length && (
                <RepetitionIcon color={Color.Secondary65} />
              )
            }
            {...(isShowTimeRepetition &&
              repetitionCount && {
                suffix: (
                  <Badge color={Color.Secondary88} count={repetitionCount} />
                ),
              })}
          >
            {title}
          </CardTitle>

          {practitionerName && (
            <KTag
              style={{ ...(isPastItem && { backgroundColor: "inherit" }) }}
              icon={<PractitionerInfoTypeIcon width={16} height={16} />}
            >
              <KMdText>{practitionerName}</KMdText>
            </KTag>
          )}
        </Flex>

        {!!(!isPastItem && upcomingAppointmentTime) && (
          <KMdTextSecondary65>
            {formatAppointmentDateStr(upcomingAppointmentTime)}
          </KMdTextSecondary65>
        )}

        {!!(isPastItem && !timeRangeSeries && startTime) && (
          <KMdTextSecondary65>
            {formatAppointmentDateStr(startTime)}
          </KMdTextSecondary65>
        )}

        {!!(isShowTimeRepetition && nextSeries) && (
          <KMdTextSecondary65>Next in series: {nextSeries}</KMdTextSecondary65>
        )}

        {!!(isShowTimeRepetition && timeRangeSeries) && (
          <KMdTextSecondary65>{timeRangeSeries}</KMdTextSecondary65>
        )}
      </CardItemContainer>
    </CardItemStyled>
  );
};
